import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ExternalLink, Hash, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { useAuth } from "@/scripts/AuthContext";
import { joinChannel } from "@/lib/dmApi";
import { toastError } from "@/lib/toast";
import DmThread from "@/components/dm/DmThread";

/**
 * Bounded embed of a team's default channel on the Feed tab.
 */
export default function TeamDefaultChannel({ orgId, channel, onJoined }) {
  const { accessToken } = useAuth();
  const [joining, setJoining] = useState(false);
  const [liveChannel, setLiveChannel] = useState(channel);

  useEffect(() => {
    setLiveChannel(channel);
  }, [channel]);

  const conversationId =
    liveChannel?.conversation_id ?? liveChannel?.conversationId ?? null;
  const isMember = liveChannel?.is_member !== false;
  const canPreview =
    liveChannel?.visibility === "public" || liveChannel?.can_preview === true;
  const messagesHref =
    orgId && conversationId
      ? `/o/${encodeURIComponent(orgId)}/messages/${conversationId}`
      : null;

  if (!orgId || !conversationId) return null;

  const handleJoin = async () => {
    if (!accessToken || joining) return;
    setJoining(true);
    try {
      const joined = await joinChannel(accessToken, orgId, conversationId);
      const next = { ...liveChannel, ...joined, is_member: true };
      setLiveChannel(next);
      onJoined?.(next);
    } catch (err) {
      toastError(err, "Could not join");
    } finally {
      setJoining(false);
    }
  };

  const showThread = isMember || canPreview;

  return (
    <div className="rounded-lg border border-border bg-muted/20 overflow-hidden">
      <div className="flex flex-wrap items-center justify-between gap-2 px-3 py-2 border-b border-border">
        <div className="flex min-w-0 items-center gap-2">
          <Hash className="h-4 w-4 shrink-0 text-muted-foreground" />
          <h3 className="text-sm font-semibold text-foreground truncate">
            {liveChannel.title || "Team channel"}
          </h3>
          <Badge variant="secondary" className="text-[10px] font-normal">
            Default
          </Badge>
        </div>
        <div className="flex items-center gap-1.5 shrink-0">
          {!isMember ? (
            <Button
              type="button"
              size="sm"
              variant="outline"
              className="h-8"
              disabled={joining}
              onClick={handleJoin}
            >
              {joining ? (
                <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />
              ) : null}
              Join
            </Button>
          ) : null}
          {messagesHref ? (
            <Button type="button" variant="ghost" size="sm" className="h-8" asChild>
              <Link to={messagesHref} state={{ channel: liveChannel }}>
                <ExternalLink className="h-3.5 w-3.5 mr-1.5" />
                Open in Messages
              </Link>
            </Button>
          ) : null}
        </div>
      </div>
      {showThread ? (
        <div className="h-[36rem] min-h-[24rem] max-h-[36rem] overflow-hidden flex flex-col">
          <DmThread
            orgId={orgId}
            conversationId={conversationId}
            conversation={liveChannel}
            compact
            showPeerHeader={false}
            className="h-full min-h-0"
          />
        </div>
      ) : (
        <p className="px-3 py-4 text-sm text-muted-foreground">
          Join this channel to see the conversation.
        </p>
      )}
    </div>
  );
}
